import { createContext, useCallback, useEffect, useState } from "react";

import {
  BulkNotification,
  NotificationStatus,
} from "@/lib/components/Menu/types/types";
import { useAuth, useAxios } from "@/lib/hooks";

type NotificationsContextType = {
  isVisible: boolean;
  setIsVisible: React.Dispatch<React.SetStateAction<boolean>>;
  bulkNotifications: BulkNotification[];
  setBulkNotifications: React.Dispatch<
    React.SetStateAction<BulkNotification[]>
  >;
  updateNotifications: () => Promise<void>;
  unreadNotifications?: number;
  setUnreadNotifications?: React.Dispatch<React.SetStateAction<number>>;
};

export const NotificationsContext = createContext<
  NotificationsContextType | undefined
>(undefined);

export const NotificationsProvider = ({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element => {
  const [isVisible, setIsVisible] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [bulkNotifications, setBulkNotifications] = useState<
    BulkNotification[]
  >([]);
  const { axiosInstance } = useAxios();
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();

  const updateNotifications = useCallback(async () => {
    if (!isAuthenticated) {
      return;
    }

    try {
      const response = await axiosInstance.get<BulkNotification[]>(
        "/notifications/bulk",
      );
      const bulkNotifs = response.data;

      setBulkNotifications(bulkNotifs);
      setUnreadNotifications(
        bulkNotifs.filter((bulk) => !bulk.notifications[0]?.read).length,
      );
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    }
  }, [axiosInstance, isAuthenticated]);

  useEffect(() => {
    if (!isVisible || !isAuthenticated) {
      return;
    }

    const markNotificationsAsRead = async () => {
      const notificationsToMarkRead: string[] = bulkNotifications
        .filter((bulk) =>
          bulk.notifications.every(
            (notif) => notif.status !== NotificationStatus.Info,
          ),
        )
        .flatMap((bulk) =>
          bulk.notifications
            .filter((notif) => !notif.read)
            .map((notif) => notif.id),
        );

      if (notificationsToMarkRead.length > 0) {
        try {
          await axiosInstance.post("/notifications/mark-read", {
            notification_ids: notificationsToMarkRead,
          });
          await updateNotifications();
        } catch (error) {
          console.error("Failed to mark notifications as read:", error);
        }
      }
    };

    void markNotificationsAsRead();
  }, [
    isVisible,
    bulkNotifications,
    axiosInstance,
    updateNotifications,
    isAuthenticated,
  ]);

  useEffect(() => {
    if (isAuthLoading || !isAuthenticated) {
      return;
    }

    void updateNotifications();

    const interval = setInterval(() => {
      void updateNotifications();
    }, 30000);

    return () => clearInterval(interval);
  }, [updateNotifications, isAuthenticated, isAuthLoading]);

  return (
    <NotificationsContext.Provider
      value={{
        isVisible,
        setIsVisible,
        bulkNotifications,
        setBulkNotifications,
        updateNotifications,
        unreadNotifications,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};
